import React, { useState } from 'react';
import { Typography, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { CloseIcon, StacklineDefaultProfileImage } from 'src/components/SvgIcons/SvgIcons';
import colors from 'src/utils/colors';
import { apiGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/api/profileApis';
import { GetProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import { styled } from '@mui/material/styles';
import { fetchUserMetaData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ViewAdjustmentModal/utils';
import { KeywordsList } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Library/components/KeywordsList';
import moment from 'moment';
import { getSummaryText } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/RequirementCards/utils';
import { useFormattedData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useFormattedData';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { getInitials } from 'src/utils/stringFormatting';

interface ContentProfileModalProps {
  profileId: string;
  profileVersion?: number;
  children: React.ReactNode; // trigger
}

const MiniColumn = styled(Flex)<{ width?: string }>(({ width }) => {
  return {
    width,
    flexDirection: 'column',
    gap: 4
  };
});

const MiniColumnHeader = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: colors.grey
});

export const ContentProfileModal: React.FC<ContentProfileModalProps> = ({ profileId, profileVersion, children }) => {
  const [open, setOpen] = useState(false);
  const categories = useFormattedData('category');
  const subCategories = useFormattedData('sub-category');
  const { beaconNavigator } = useBeaconNavigator();
  const setStoreViewMode = useProfileSummaryStore((s) => s.setViewMode);
  const setStoreEditingId = useProfileSummaryStore((s) => s.setEditingProfileId);
  const modalBodyTextSx = { fontSize: '14px' as const };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['contentProfile', profileId, profileVersion],
    queryFn: async () => {
      const res = await apiGetSingleProfile({
        profileId,
        version: profileVersion ? String(profileVersion) : undefined
      });
      return res.data.data as GetProfile;
    },
    enabled: !!profileId
  });

  const { data: ownerMeta } = useQuery({
    queryKey: ['userMeta', data?.createdBy],
    queryFn: async () => {
      const res = await fetchUserMetaData({ userIds: [data?.createdBy as string] });
      return res.data as Record<string, { firstName: string; lastName: string; email?: string }>;
    },
    enabled: !!data?.createdBy
  });
  const createdByUser = data && ownerMeta ? ownerMeta[data.createdBy] : undefined;
  const inclusionKeywords = (data?.settings?.keywords?.inclusion ?? []).filter((k) => k.trim().length > 0);
  const exclusionKeywords = (data?.settings?.keywords?.exclusion ?? []).filter((k) => k.trim().length > 0);

  return (
    <>
      <span
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {children}
      </span>

      <GenericModal
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '-webkit-font-smoothing': 'antialiased',
          '& .MuiPaper-root': {
            width: '752px',
            height: '641px',
            border: `1px solid ${colors.graysSecondary}`
          },
          '& .genericModal__body': {
            gap: '24px',
            padding: '24px 48px',
            height: '497px',
            overflowY: 'auto'
          },
          '& .genericModal__footer': {
            padding: '20px 48px'
          }
        }}
      >
        <Flex alignItems="center" className="genericModal__header">
          <Text
            variant="h4"
            sx={{
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            View Content Profile
          </Text>
          <CloseIcon
            style={{ cursor: 'pointer', width: '24px', height: '24px', marginBottom: 'auto' }}
            onClick={() => setOpen(false)}
          />
        </Flex>
        <Divider
          sx={{
            marginTop: '22px',
            position: 'relative',
            left: '-100px',
            width: '1000px',
            borderColor: colors.graysSecondary
          }}
        />
        <GenericModal.Body>
          <Flex flexDirection="column" gap={24}>
            <Text variant="body1" sx={{ fontWeight: 500 }}>
              Content Profile
            </Text>
            <Flex gap={40}>
              <MiniColumn width="128px">
                <MiniColumnHeader>Owner</MiniColumnHeader>
                <Flex gap={8} alignItems="center">
                  <AppProfileIcon
                    userId={
                      createdByUser?.firstName && createdByUser?.lastName ? data?.createdBy : undefined
                    }
                    size={30}
                    initials={
                      createdByUser?.firstName && createdByUser?.lastName
                        ? getInitials(`${createdByUser.firstName} ${createdByUser.lastName}`)
                        : 'SL'
                    }
                    customIcon={
                      !(createdByUser?.firstName && createdByUser?.lastName) ? (
                        <StacklineDefaultProfileImage />
                      ) : undefined
                    }
                  />
                  <Text
                    variant="body3"
                    sx={{
                      ...modalBodyTextSx,
                      fontWeight: 400,
                      width: '78px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {createdByUser?.firstName && createdByUser?.lastName
                      ? `${createdByUser.firstName} ${createdByUser.lastName}`
                      : 'Stackline'}
                  </Text>
                </Flex>
              </MiniColumn>
              <MiniColumn>
                <MiniColumnHeader>Name</MiniColumnHeader>
                <Text
                  variant="body3"
                  sx={{
                    ...modalBodyTextSx,
                    margin: 'auto 0px'
                  }}
                >
                  {data?.profileName}
                </Text>
              </MiniColumn>
              <MiniColumn>
                <MiniColumnHeader>Date Edited</MiniColumnHeader>
                <Text
                  variant="body3"
                  sx={{
                    ...modalBodyTextSx,
                    margin: 'auto 0px'
                  }}
                >
                  {data?.updatedAt ? moment(data?.updatedAt).format('MMM D, YYYY') : 'N/A'}
                </Text>
              </MiniColumn>
            </Flex>

            <Flex gap={64}>
              <MiniColumn width="102px">
                <MiniColumnHeader>Title Limit</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.characterLimits?.titles ?? 'N/A'} characters
                </Text>
              </MiniColumn>
              <MiniColumn>
                <MiniColumnHeader>Bullet Count</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.bulletPointsCount ?? 'N/A'} bullets
                </Text>
              </MiniColumn>
              <MiniColumn>
                <MiniColumnHeader>Bullet Limit</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.characterLimits?.bullets ?? 'N/A'} characters
                </Text>
              </MiniColumn>
              <MiniColumn width="110px">
                <MiniColumnHeader>Description Limit</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.characterLimits?.descriptions ?? 'N/A'} characters
                </Text>
              </MiniColumn>
            </Flex>

            {data?.settings?.aiInstructions && data.settings.aiInstructions !== '' && (
              <Flex>
                <MiniColumn width="100%">
                  <MiniColumnHeader>AI Custom Instructions</MiniColumnHeader>
                  <Text variant="body3" sx={modalBodyTextSx}>
                    {data.settings.aiInstructions}
                  </Text>
                </MiniColumn>
              </Flex>
            )}

            <Flex gap={68}>
              <MiniColumn>
                <MiniColumnHeader>Dominant Voice</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.voice?.type ?? 'N/A'}
                </Text>
              </MiniColumn>
              <MiniColumn>
                <MiniColumnHeader>Dominant Tone</MiniColumnHeader>
                <Text variant="body3" sx={{ ...modalBodyTextSx, color: colors.darkBlue }}>
                  {data?.settings?.tone?.type ?? 'N/A'}
                </Text>
              </MiniColumn>
            </Flex>

            {inclusionKeywords.length > 0 && (
              <MiniColumn>
                <MiniColumnHeader>Keyword & Phrases Inclusion List</MiniColumnHeader>
                <KeywordsList variant="inclusion" keywords={inclusionKeywords} />
              </MiniColumn>
            )}

            {exclusionKeywords.length > 0 && (
              <MiniColumn>
                <MiniColumnHeader>Keyword & Phrases Exclusion List</MiniColumnHeader>
                <KeywordsList variant="exclusion" keywords={exclusionKeywords} />
              </MiniColumn>
            )}

            {data?.settings?.guidelines && data.settings.guidelines.length > 0 && (
              <Flex flexDirection="column" gap={24}>
                {data.settings.guidelines.map((g, idx) => {
                  let targetName: string | undefined;
                  if (g.type === 'AvoidCrossCategoryComparisons') {
                    const { comparisonType, target } = (g as any).parameters;
                    const list = comparisonType === 'category' ? categories : subCategories;
                    const found = list.find((item: any) => item.id === target);
                    targetName = found?.label || target;
                  }
                  const summary = getSummaryText(g as any, targetName ? { targetName } : undefined);
                  return (
                    <MiniColumn key={`${g.type}-${idx}`}>
                      <MiniColumnHeader>Requirement {idx + 1}</MiniColumnHeader>
                      <Text
                        key={`${g.type}-${idx}`}
                        variant="body3"
                        sx={{ ...modalBodyTextSx, color: colors.darkBlue }}
                      >
                        {summary}
                      </Text>
                    </MiniColumn>
                  );
                })}
              </Flex>
            )}
          </Flex>
        </GenericModal.Body>
        <GenericModal.Footer>
          <Flex justifyContent="space-between">
            <SlButton
              variant="normal"
              onClick={() => setOpen(false)}
              buttonSx={{ border: `1px solid ${colors.graysSecondary}` }}
            >
              Close
            </SlButton>
            <SlButton
              variant="contained"
              onClick={() => {
                setStoreViewMode('edit');
                setStoreEditingId(profileId);
                beaconNavigator.updateSearchParams({
                  tab: BEACON_TABS.PROFILES,
                  subtab: BEACON_SUBTABS.PROFILES_SETTINGS,
                  mode: 'edit',
                  profileId
                });
                beaconNavigator.go();
              }}
            >
              Edit Profile
            </SlButton>
          </Flex>
        </GenericModal.Footer>
      </GenericModal>
    </>
  );
};

export default ContentProfileModal;
